import axios from 'axios'
import cuid from 'cuid'
import qs from 'qs'
import { captureException } from '@sentry/browser'
import toast from 'react-hot-toast'

const instance = axios.create()

const serialize = (params) => {
  return qs.stringify(params, {
    arrayFormat: 'brackets',
    encodeValuesOnly: true
  })
}

const handleSuccesfulResponse = (response) => {
  const headers = response.headers
  const store = window.store || window.booqableStore
  const state = store.getState()

  const token = headers['Session-Expiry-Token'] || headers['session-expiry-token']
  const clientVersion = headers['Client-Version'] || headers['client-version']
  const readOnlyMode = (headers['X-Read-Only-Mode'] || headers['x-read-only-mode']) === 'true'

  if (state.session?.token !== token) {
    store.dispatch({ type: 'SET_TOKEN', payload: { token } })
  }

  if (state.session?.clientVersion !== clientVersion) {
    store.dispatch({ type: 'SET_CLIENT_VERSION', payload: { client_version: clientVersion } })
  }

  if (state.session?.readOnlyMode !== readOnlyMode) {
    store.dispatch({ type: 'SET_READ_ONLY_MODE', payload: { read_only_mode: readOnlyMode } })
  }

  return response
}

const handleUnauthorizedResponse = () => {
  window.skipUnsavedChangesCheck = true
  window.location.reload()
}

const handleServiceUnavailableResponse = (error) => {
  window.store?.dispatch({
    type: 'SERVICE_UNAVAILABLE_ERROR',
    payload: { error: error.response.data }
  })
}

const showSupportErrorMessage = (error) => {
  // Show detailed error messages to support staff
  if (!window.isBooqableSupport) return

  const { config, response } = error
  const method = config?.method?.toUpperCase() || 'UNKNOWN'
  const url = config?.url || 'unknown URL'
  const status = response?.status || 'unknown status'

  let responseData = ''
  if (response?.data) {
    // Check if it's the standard API error format
    if (response.data.errors && Array.isArray(response.data.errors)) {
      const errorDetails = response.data.errors.map((err) => {
        const title = err.title || 'Error'
        const detail = err.detail || 'No details available'
        const attribute = err.meta?.attribute ? ` (${err.meta.attribute})` : ''
        return `${title}${attribute}: ${detail}`
      }).join('\n')
      responseData = errorDetails
    } else if (typeof response.data === 'string') {
      responseData = response.data
    } else {
      try {
        responseData = JSON.stringify(response.data)
      } catch (_e) {
        responseData = '[Unable to stringify response data]'
      }
    }
  }

  const errorMessage = `HTTP Error: ${method} ${url} (${status})\n${responseData}`
  toast.error(errorMessage, {
    duration: 8000, // Show for 8 seconds to give support time to read
    style: {
      background: '#1a1a1a',
      color: '#00ff00',
      border: '2px solid #00ff00',
      fontFamily: 'monospace',
      fontSize: '12px'
    },
    iconTheme: {
      primary: '#00ff00',
      secondary: '#1a1a1a'
    }
  })
}

const handle500Errors = (error) => {
  captureException(error, error.response)
  showSupportErrorMessage(error)
}

const handleFailureResponse = (error) => {
  const status = error?.response?.status

  if (status === 400) showSupportErrorMessage(error)

  if (status === 401) handleUnauthorizedResponse()

  if (status === 422) showSupportErrorMessage(error)

  if (status >= 500) handle500Errors(error)

  if (status === 503) handleServiceUnavailableResponse(error)

  return Promise.reject(error)
}

// Format nested params correctly
instance.interceptors.request.use((config) => {
  config.paramsSerializer = { serialize }

  return config
})

// This header allows pusher/orm.js
// to ignore events triggered by the current browser window
instance.interceptors.request.use((config) => {
  window.booqablePusherSessionId ||= cuid()

  config.headers.set('X-Booqable-Pusher-Session-Id', window.booqablePusherSessionId)

  return config
})

// Inject the storefront token
instance.interceptors.request.use((config) => {
  if (window.fingerprint) {
    config.headers.set('X-Booqable-Storefront-Token', window.fingerprint)
  }

  return config
})

// Inject the CSRF token
instance.interceptors.request.use((config) => {
  const tokenElement = document.querySelector('meta[name="csrf-token"]')
  if (tokenElement) {
    const token = tokenElement.getAttribute('content')

    config.headers.set('X-CSRF-Token', token)
  }

  return config
})

instance.interceptors.response.use(
  handleSuccesfulResponse,
  handleFailureResponse
)

export default instance
